import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Modal, Breadcrumb, Tag } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';

import { logout } from '~actions/auth';
import * as accountActions from '~actions/account';

import { LogoMain, AccountPhoto } from 'common/components/svgs';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 56,
    borderBottom: '1px solid rgba(0,0,0,.09)',
  },
  headerLeft: {
    flex: 1,
    display: 'flex',
    paddingLeft: 24,
  },
  logo: {
    height: 24,
    color: theme.colors.yellow6,
    fill: '#4C4C4C',
  },
  articleInfo: {
    flex: 1,
    paddingLeft: 24,
    marginLeft: 24,
    borderLeft: `1px solid ${theme.colors.gray5}`,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  statusTag: {
    fontSize: 12,
    lineHeight: '20px',
    color: theme.colors.blue6,
    padding: '2px 8px',
    marginRight: 20,
    borderRadius: 2,
    border: 'none',
    backgroundColor: theme.colors.blue1,

    '&.draft': {
      color: theme.colors.gray7,
      backgroundColor: theme.colors.gray3,
    },

    '&.published': {
      color: theme.colors.mainGreen6,
      backgroundColor: theme.colors.mainGreen1,
    },

    '&:hover': {
      opacity: 1,
    },
  },
  userInfo: {
    display: 'flex',
    height: '100%',
    padding: '17px 20px',
    borderLeft: '1px solid #e7e7e7',
  },
  userIcon: {
    width: 20,
    height: 20,
  },
  userText: {
    paddingLeft: 8,
    ...theme.typography.body2,
    color: '#4C4C4C',
  },
  logout: {
    height: '100%',
    padding: '17px 20px',
    borderLeft: '1px solid #e7e7e7',
    cursor: 'pointer',
  },
  logoutText: {
    ...theme.typography.h5,
    color: '#9E9E9E',
  },
  breadCrumbText: {
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

const Header = ({ type, title, articleTypeIndex, index, status }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { cmsUser } = useSelector(state => ({
    cmsUser: state.dashboard.account?.toJS().account,
  }));

  const { firstName, lastName } = cmsUser ?? {};

  const handleLogoutModal = () => {
    return Modal.confirm({
      className: '',
      icon: <ExclamationCircleFilled />,
      title: t('cms.header.dropdown.menu.log_out.modal.title'),
      okText: t('cms.form.pop_confirm.yes'),
      cancelText: t('cms.form.pop_confirm.no'),
      width: 380,
      onOk: () => dispatch(logout()),
    });
  };

  useEffect(() => {
    dispatch(accountActions.getCmsUser());
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.headerLeft}>
        <LogoMain className={classes.logo} />

        <div className={classes.articleInfo}>
          <Breadcrumb separator=">">
            <Breadcrumb.Item>{`${articleTypeIndex}. ${t(
              `content.article_type.${type?.toLowerCase()}`,
            )}`}</Breadcrumb.Item>
            <Breadcrumb.Item
              className={classes.breadCrumbText}
            >{`${articleTypeIndex}.${index}.${title}`}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div className={classes.headerRight}>
        <Tag className={`${classes.statusTag} ${status?.toLowerCase()}`}>
          {t(`content.article_status.${status?.toLowerCase()}`)}
        </Tag>
        <div className={classes.userInfo}>
          <AccountPhoto className={classes.userIcon} />
          <span className={classes.userText}>
            {firstName}&nbsp;{lastName}
          </span>
        </div>
        <div className={classes.logout} onClick={handleLogoutModal}>
          <span className={classes.logoutText}>{t('cms.header.dropdown.menu.log_out')}</span>
        </div>
      </div>
    </div>
  );
};

export default Header;
